import { useObserver } from 'mobx-react';
import React, { useEffect } from 'react';

import { useStores } from '../hooks/useStores';
import config from '../utils/config';

function useStoreData() {
    const { store } = useStores();
    return useObserver(() => ({
        currentTrack: store.playback.currentTrack,
        setProgress: store.playback.setProgress,
        progress: store.playback.progress,
        isPlaying: store.playback.config.isPlaying,
        currentTrackColors: store.config.currentTrackColors,
    }));
}

const TrackProgressBar = () => {
    const { currentTrack, setProgress, progress, isPlaying, currentTrackColors } = useStoreData();
    const progressFrequency = config.PLAYBACK.PROGRESS_BAR_FREQUENCY;
    const radio = 49;
    const stroke = 1.3;
    const progressColor = currentTrackColors[1];
    const backgroundColor = currentTrackColors[2];
    const circunference = 2 * Math.PI * radio;

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (isPlaying && currentTrack) {
            timer = setInterval(() => {
                if (progress < progressFrequency) {
                    setProgress(progress + 1);
                }
            }, currentTrack.track.duration / progressFrequency);
        } else if (progress === progressFrequency || !isPlaying) {
            if (timer) {
                clearInterval(timer);
            }
        }

        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying, progress]);

    return (
        <figure style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            <svg
                className="Spinner-svg"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMinYMin meet"
                shapeRendering="geometricPrecision"
            >
                <circle
                    strokeMiterlimit={stroke}
                    cx="50"
                    cy="50"
                    r={radio}
                    stroke={backgroundColor}
                    fill="transparent"
                    strokeOpacity=".3"
                    strokeWidth={stroke}
                    strokeDasharray={`${circunference}, ${circunference}`}
                />
                <circle
                    style={{ transition: 'stroke-dasharray .3s ease' }}
                    strokeMiterlimit={stroke}
                    cx="50"
                    cy="50"
                    r={radio}
                    stroke={progressColor}
                    fill="transparent"
                    strokeLinecap="round"
                    strokeWidth={stroke}
                    strokeDasharray={`${Math.round((circunference * progress) / progressFrequency)}, ${circunference}`}
                >
                    <animateTransform
                        attributeName="transform"
                        attributeType="XML"
                        dur={'150s'}
                        type="rotate"
                        repeatCount={progress === 100 ? 'none' : 'indefinite'}
                        from="0 50 50"
                        to="360 50 50"
                    />
                </circle>
            </svg>
        </figure>
    );
};

export default TrackProgressBar;
